import { FC, useCallback, useEffect, useState } from 'react';
import { useModuleContext } from '@/context/module';
import { FormikState } from 'formik';
import useNotification from '@/hooks/notifications/useNotification';
import { IProfileState } from '@/states/profile';
import useUsersApi from '@/hooks/user/useUsersApi';
import PasswordEditForm, { PasswordEditFormFields } from './PasswordEditForm';
import useAuth from '@/hooks/useAuth';
import GenericDialogV2 from '@/components/common/GenericDialogV2';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';

const MODAL_TITLE = 'Cambiar contraseña';
const MODAL_SUBTITLE = 'Actualiza tu contraseña para mantener tu cuenta segura.';
const INVALID_CREDENTIAL_MESSAGE = 'La contraseña actual es incorrecta';

type ErrorHandlerErrorType = Error & { errorMessages: string[] };
const formId = 'password-edit-form-id';

type PasswordEditProps = {
    open?: boolean
    onClose?: () => void;
}

const PasswordEdit: FC<PasswordEditProps> = ({ open = false, onClose }: PasswordEditProps) => {
    const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);
    const [wrongCredentials, setWrongCredentials] = useState<boolean>(false);
    const {
        state: { user },
    } = useModuleContext<IProfileState>();
    const { token } = useUsersApi();
    const { changePassword } = useAuth()

    useEffect(() => {
        if (wrongCredentials) {
            const timer = setTimeout(() => {
                setWrongCredentials(false);
            }, 10000);

            return () => clearTimeout(timer);
        }
    }, [wrongCredentials]);

    const notify = useNotification();

    const replaceCognitoMessage = (message: string): string => {
        const replaced = message.replace('Incorrect username or password', INVALID_CREDENTIAL_MESSAGE);
        setWrongCredentials(replaced.indexOf(INVALID_CREDENTIAL_MESSAGE) !== -1);

        return replaced;
    };

    const handleError = (err: ErrorHandlerErrorType) => {

        let errorMessage = 'Ups!!! Algo salio mal.';
        if (
            'errorMessages' in err &&
            Array.isArray((err as ErrorHandlerErrorType).errorMessages)
        ) {
            (err as ErrorHandlerErrorType).errorMessages.forEach(
                (message: string) => {
                    notify.error(message);
                }
            );
        } else {

            try {
                errorMessage = err.message;
            } catch (e) {
                console.error('Error parsing error message:', e);
            }
        }
        notify.error(replaceCognitoMessage(errorMessage));
    };

    const handleAddOrEdit = useCallback(
        async (
            fields: PasswordEditFormFields,
            resetForm?: (
                nextState?: Partial<FormikState<PasswordEditFormFields>>
            ) => void
        ) => {
            switch ('edit') {
                case 'edit':
                    handleChangePassword(fields)
                        .then(() => {
                            notify.success('La contraseña ha sido cambiada');
                            resetForm && resetForm();
                            onClose && onClose();
                        })
                        .catch(handleError)
                        .finally(() => setLoadingUpdate(false));
                    break;
                default:
                // Nothing
            }
        },
        [token, user]
    );

    const handleChangePassword = useCallback(
        (data: PasswordEditFormFields) => {
            // eslint-disable-next-line no-async-promise-executor
            return new Promise<void>(async (resolve, reject) => {
                try {
                    if (token) {
                        setLoadingUpdate(true);

                        await changePassword(data.oldPassword, data.newPassword);

                        setLoadingUpdate(false);

                        resolve();
                    }
                } catch (err) {
                    reject(err);
                    setLoadingUpdate(false);
                }
            });
        },
        [token]
    );

    if (!open) return (<></>);

    return (
        <GenericDialogV2
            maxWidth="md"
            title={MODAL_TITLE}
            titleIcon={<VerifiedUserOutlinedIcon color='primary' fontSize='small' />}
            subTitle={MODAL_SUBTITLE}
            hideActions={true}
            open={open}
            onClose={() => onClose && onClose()}
        >
            <PasswordEditForm
                wrongCredentials={wrongCredentials}
                submitAction={handleAddOrEdit}
                formId={formId}
                loadingUpdate={loadingUpdate}
                onCancel={onClose}
            />
        </GenericDialogV2>
    );
};

export default PasswordEdit;
